import type { CSSProperties, MouseEvent } from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { Button, ButtonVariation } from '../Button';
import { QuestionMarkIcon } from '../Icon';
import { ASSET_PATHS } from '../../utils/assetPath';
import './HeaderHelpPopover.css';

const CMS_SERVICE_DESK_EMAIL = 'CMS_IT_Service_Desk@cms.hhs.gov';
const HOVER_CLOSE_DELAY_MS = 200;
const POPOVER_VIEWPORT_MARGIN = 16;
const POPOVER_TRIGGER_GAP = 8;

interface HeaderHelpPopoverProps {
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function HeaderHelpPopover({ 'data-testid': dataTestId }: HeaderHelpPopoverProps) {
  const baseTestId = dataTestId ?? 'header-help';
  const generatedId = useId();
  const popoverId = `${baseTestId}-${generatedId.replace(/:/g, '')}-popover`;
  const headingId = `${popoverId}-heading`;
  const triggerGroupRef = useRef<HTMLDivElement>(null);
  const iconTriggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null);
  const hoverCloseTimerRef = useRef<number | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isHoverSuppressed, setIsHoverSuppressed] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({
    arrowLeft: 0,
    isPositioned: false,
    left: 0,
    top: 0,
  });
  const isOpen = isPinned || (isHovering && !isHoverSuppressed);

  const clearHoverCloseTimer = useCallback(() => {
    if (hoverCloseTimerRef.current !== null) {
      window.clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }, []);

  const positionPopover = useCallback(() => {
    if (!triggerGroupRef.current || !iconTriggerRef.current || !popoverRef.current) {
      return;
    }

    const triggerRect = triggerGroupRef.current.getBoundingClientRect();
    const iconRect = iconTriggerRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const iconCenter = iconRect.left + iconRect.width / 2;
    const preferredLeft = iconCenter - popoverRect.width / 2;
    const maximumLeft = Math.max(
      POPOVER_VIEWPORT_MARGIN,
      window.innerWidth - popoverRect.width - POPOVER_VIEWPORT_MARGIN,
    );
    const left = Math.min(Math.max(preferredLeft, POPOVER_VIEWPORT_MARGIN), maximumLeft);

    setPopoverPosition({
      arrowLeft: iconCenter - left,
      isPositioned: true,
      left,
      top: triggerRect.bottom + POPOVER_TRIGGER_GAP,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    positionPopover();
  }, [isOpen, positionPopover]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleViewportChange = () => positionPopover();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen, positionPopover]);

  const dismissPopover = useCallback((restoreFocus = false) => {
    setIsPinned(false);
    setIsHoverSuppressed(true);

    if (restoreFocus) {
      window.requestAnimationFrame(() => lastTriggerRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismissPopover(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dismissPopover, isOpen]);

  useEffect(() => clearHoverCloseTimer, [clearHoverCloseTimer]);

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    lastTriggerRef.current = event.currentTarget;

    if (isPinned) {
      dismissPopover();

      return;
    }

    setIsHoverSuppressed(false);
    setIsPinned(true);
  };

  const handleMouseEnter = () => {
    clearHoverCloseTimer();
    setIsHoverSuppressed(false);
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    clearHoverCloseTimer();
    hoverCloseTimerRef.current = window.setTimeout(() => {
      setIsHovering(false);
      setIsHoverSuppressed(false);
      hoverCloseTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  const popoverStyle = {
    '--header-help-arrow-left': `${popoverPosition.arrowLeft}px`,
    left: `${popoverPosition.left}px`,
    top: `${popoverPosition.top}px`,
    visibility: popoverPosition.isPositioned ? 'visible' : 'hidden',
  } as CSSProperties;

  return (
    <div
      className='header-help'
      data-testid={baseTestId}>
      <div
        ref={triggerGroupRef}
        className='header-help__triggers'
        data-testid={`${baseTestId}-triggers`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <button
          aria-controls={popoverId}
          aria-expanded={isOpen}
          aria-haspopup='dialog'
          className='header-help__text-trigger'
          data-testid={`${baseTestId}-text-trigger`}
          type='button'
          onClick={handleTriggerClick}>
          Get Help
        </button>
        <button
          ref={iconTriggerRef}
          aria-controls={popoverId}
          aria-expanded={isOpen}
          aria-haspopup='dialog'
          aria-label='Get Help contact information'
          className='header-help__icon-trigger action-icon--blue-round'
          data-testid={`${baseTestId}-icon-trigger`}
          type='button'
          onClick={handleTriggerClick}>
          <QuestionMarkIcon size={24} />
        </button>
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          aria-labelledby={headingId}
          className='header-help__popover'
          data-testid={`${baseTestId}-popover`}
          id={popoverId}
          role='dialog'
          style={popoverStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <button
            aria-label='Close Get Help'
            className='header-help__close'
            data-testid={`${baseTestId}-close`}
            type='button'
            onClick={() => dismissPopover(true)}>
            <img
              alt=''
              aria-hidden='true'
              src={ASSET_PATHS.CLOSE_ICON}
            />
          </button>
          <h2
            className='header-help__heading'
            id={headingId}>
            Need Help?
          </h2>
          <p className='header-help__description'>Contact the CMS IT Service Desk:</p>
          <div className='header-help__phone-numbers'>
            <strong>1-800-562-1963</strong>
            <strong>410-786-2580</strong>
          </div>
          <Button
            bordered
            isAutoWidth
            className='header-help__email'
            data-testid={`${baseTestId}-email`}
            href={`mailto:${CMS_SERVICE_DESK_EMAIL}`}
            size='small'
            variation={ButtonVariation.GHOST}>
            Email CMS IT Service Desk
          </Button>
        </div>
      )}
    </div>
  );
}

export default HeaderHelpPopover;
